/**
 * 환경설정 — 로그인 기관 사이드바 로고 등록
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme as staticTheme } from 'styles/theme';
import { ActionButton } from 'styles/common.styles';
import {
  loadTenantLogo,
  saveTenantLogo,
  removeTenantLogo,
  readImageFileAsDataUrl,
  TENANT_LOGO_MAX_BYTES,
} from 'system/tenantLogoStore';
import { showAlert } from 'utils/toastBridge';

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
  line-height: 1.45;
`;

const TenantHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.primaryLight};
  color: ${(p) => p.theme.colors.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
  font-weight: 600;
  width: fit-content;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Label = styled.label`
  min-width: 72px;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.secondary};
  text-align: right;
`;

const TextInput = styled.input`
  min-width: 200px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.sm};
  background: ${(p) => p.theme.colors.background.input};
  color: ${(p) => p.theme.colors.text.primary};
  font-size: ${staticTheme.typography.fontSize.sm};
`;

const PreviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 240px;
  height: 48px;
  padding: 6px 12px;
  border: 1px dashed ${(p) => p.theme.colors.border};
  border-radius: ${staticTheme.borderRadius.md};
  background: ${(p) => (p.$dark ? 'var(--erp-color-sidebar-dark, #1e293b)' : p.theme.colors.background.sidebar)};
`;

const PreviewImg = styled.img`
  max-height: 28px;
  max-width: 200px;
  object-fit: contain;
  object-position: center;
`;

const EmptyPreview = styled.span`
  font-size: ${staticTheme.typography.fontSize.xs};
  color: ${(p) => p.theme.colors.text.tertiary};
`;

const HiddenFile = styled.input`
  display: none;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${staticTheme.typography.fontSize.sm};
  color: ${(p) => p.theme.colors.text.secondary};
  cursor: pointer;
  user-select: none;
`;

/**
 * @param {{ tenantCode?: string }} props
 */
const TenantLogoUploadPanel = ({ tenantCode = '' }) => {
  const fileRef = useRef(null);
  const code = String(tenantCode || '').trim().toUpperCase();
  const [logoAlt, setLogoAlt] = useState('');
  const [logoAreaDark, setLogoAreaDark] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [pendingDataUrl, setPendingDataUrl] = useState('');
  const [hasUpload, setHasUpload] = useState(false);

  const refreshFromStore = useCallback((orgCode) => {
    const entry = loadTenantLogo(orgCode);
    if (entry?.logoDataUrl) {
      setPreviewUrl(entry.logoDataUrl);
      setPendingDataUrl(entry.logoDataUrl);
      setLogoAlt(entry.logoAlt || orgCode);
      setLogoAreaDark(entry.logoAreaDark === true);
      setHasUpload(true);
    } else {
      setPreviewUrl('');
      setPendingDataUrl('');
      setLogoAlt('');
      setLogoAreaDark(false);
      setHasUpload(false);
    }
  }, []);

  useEffect(() => {
    if (code) refreshFromStore(code);
  }, [code, refreshFromStore]);

  const handlePickFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await readImageFileAsDataUrl(file);
      setPendingDataUrl(dataUrl);
      setPreviewUrl(dataUrl);
      if (!logoAlt) setLogoAlt(code);
    } catch (err) {
      showAlert(err?.message || '이미지를 읽지 못했습니다.');
    }
  }, [logoAlt, code]);

  const handleSave = useCallback(() => {
    if (!code) {
      showAlert('기관코드가 없습니다. 다시 로그인해 주세요.');
      return;
    }
    if (!pendingDataUrl) {
      showAlert('업로드할 이미지를 선택하세요.');
      return;
    }
    const result = saveTenantLogo(code, {
      logoDataUrl: pendingDataUrl,
      logoAlt: logoAlt || code,
      logoAreaDark,
    });
    if (!result.ok) {
      showAlert(result.message || '저장에 실패했습니다.');
      return;
    }
    setHasUpload(true);
    showAlert('로고를 저장했습니다. 사이드바에 바로 반영됩니다.');
  }, [code, pendingDataUrl, logoAlt, logoAreaDark]);

  const handleRemove = useCallback(() => {
    if (!code) return;
    removeTenantLogo(code);
    refreshFromStore(code);
    showAlert('업로드 로고를 삭제했습니다. (정적 기본 로고가 있으면 그로 표시됩니다)');
  }, [code, refreshFromStore]);

  return (
    <Wrap>
      <Hint>
        로그인 기관의 사이드바 상단 CI만 등록합니다. PNG(투명 배경 권장) · JPG · WebP · SVG,
        최대 약 {Math.floor(TENANT_LOGO_MAX_BYTES / 1024)}KB. 업로드본이 정적 기본 로고보다 우선합니다.
      </Hint>

      <TenantHint>기관 {code || '—'}</TenantHint>

      <Row>
        <Label htmlFor="tenant-logo-alt">표시명</Label>
        <TextInput
          id="tenant-logo-alt"
          value={logoAlt}
          onChange={(e) => setLogoAlt(e.target.value)}
          placeholder="로고 alt / 툴팁"
        />
        <ToggleLabel>
          <input
            type="checkbox"
            checked={logoAreaDark}
            onChange={(e) => setLogoAreaDark(e.target.checked)}
          />
          로고 행 다크 플레이트
        </ToggleLabel>
      </Row>

      <Row>
        <Label>미리보기</Label>
        <PreviewBox $dark={logoAreaDark}>
          {previewUrl ? (
            <PreviewImg src={previewUrl} alt={logoAlt || code || 'logo'} />
          ) : (
            <EmptyPreview>업로드된 로고 없음</EmptyPreview>
          )}
        </PreviewBox>
      </Row>

      <Row>
        <Label />
        <HiddenFile
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={handlePickFile}
        />
        <ActionButton type="button" onClick={() => fileRef.current?.click()} disabled={!code}>
          이미지 선택
        </ActionButton>
        <ActionButton
          type="button"
          $variant="primary"
          onClick={handleSave}
          disabled={!code || !pendingDataUrl}
        >
          로고 저장
        </ActionButton>
        <ActionButton type="button" onClick={handleRemove} disabled={!code || !hasUpload}>
          업로드 삭제
        </ActionButton>
      </Row>
    </Wrap>
  );
};

export default TenantLogoUploadPanel;
